// Import enumerations.
import {
	AppThemeColor,
	ButtonSize,
} from '@app/enumerations';
// Import theme.
import {
	alpha,
	Theme,
} from '@mui/material';
// Import types.
import {
	TFontSize,
} from '@app/types';

export default class StylesService {

	private static instance: StylesService;

	private constructor() {

	}

    /**
     * Get a singleton instance of StylesService.
     */
	public static getInstance(): StylesService {
		if (!StylesService.instance) {
			StylesService.instance = new StylesService();
		}
		return StylesService.instance;
	}

	public hexToRgb = (hex: string): { r: number, g: number, b: number } | undefined => {
		var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		return result ? {
			r: parseInt(result[1], 16),
			g: parseInt(result[2], 16),
			b: parseInt(result[3], 16)
		} : undefined;
	}

	public hexToRgbCSS = (hex: string, opacity: number | undefined = undefined): string => {
		let rgb: { r: number, g: number, b: number } | undefined = this.hexToRgb(hex);
		if (rgb !== undefined) {
			return opacity !== undefined
				? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`
				: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
		}
		return 'none';
	}

	public getThemeColor = (theme: Theme, color: AppThemeColor): string => {
		switch (color) {
			case AppThemeColor.PRIMARY:
				return theme.palette.primary.main;
			case AppThemeColor.SECONDARY:
				return theme.palette.secondary.main;
			case AppThemeColor.WARNING:
				return theme.palette.warning.main;
			case AppThemeColor.ERROR:
				return theme.palette.error.main;
			case AppThemeColor.SUCCESS:
				return theme.palette.success.main;
			case AppThemeColor.INFO:
			default:
				return theme.palette.info.main;
		}
	}

	public getThemeColorContrastText = (theme: Theme, color: AppThemeColor): string => {
		switch (color) {
			case AppThemeColor.PRIMARY:
				return theme.palette.primary.contrastText;
			case AppThemeColor.SECONDARY:
				return theme.palette.secondary.contrastText;
			case AppThemeColor.WARNING:
				return theme.palette.warning.contrastText;
			case AppThemeColor.ERROR:
				return theme.palette.error.contrastText;
			case AppThemeColor.SUCCESS:
				return theme.palette.success.contrastText;
			case AppThemeColor.INFO:
			default:
				return theme.palette.info.contrastText;
		}
	}

	public getThemeFontSize = (theme: Theme, fontSize: TFontSize, isMobile: boolean): string => {
		switch (fontSize) {
			case 'xxlg':
				return isMobile ? theme.spacing(7) : theme.spacing(8);
			case 'xlg':
				return isMobile ? theme.spacing(6) : theme.spacing(7);
			case 'lg':
				return isMobile ? theme.spacing(5) : theme.spacing(6);
			case 'md':
				return isMobile ? theme.spacing(4) : theme.spacing(5);
			case 'xsm':
				return theme.spacing(3);
			case 'xxsm':
				return theme.spacing(2.5);
			case 'sm':
			default:
				return isMobile ? theme.spacing(3) : theme.spacing(4);
		}
	}

	public getButtonSize = (buttonSize: ButtonSize): string => {
		switch (buttonSize) {
			case ButtonSize.EXTRA_LARGE:
				return '55px';
			case ButtonSize.LARGE:
				return '50px';
			case ButtonSize.SMALL:
				return '40px';
			case ButtonSize.EXTRA_SMALL:
				return '35px';
			case ButtonSize.MEDIUM:
			default:
				return '45px';
		}
	}
}
